import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Award, Download, Printer, ArrowLeft, ShieldAlert } from "lucide-react";
import portalApi, { portalStorage, formatPortalError } from "./portalApi";
import api from "../../lib/api";
import { PortalHeader, useLearner } from "./PortalDashboard";
import PreviewBanner from "./PreviewBanner";
import { previewStore } from "./previewStore";

export default function PortalCertificate() {
  const learner = useLearner();
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const inPreview = previewStore.isActive();

  useEffect(() => {
    if (!learner) return;
    if (inPreview) {
      const sample = previewStore.getSampleCert();
      if (sample) setCert(sample);
      return;
    }
    portalApi.get(`/portal/certificates/${certId}`).then(r=>setCert(r.data)).catch(err=>toast.error(formatPortalError(err)));
  }, [learner, certId, inPreview]);

  const download = async () => {
    try {
      let blob;
      if (inPreview) {
        const r = await api.get("/admin/portal/preview/certificate.pdf", { responseType: "blob" });
        blob = new Blob([r.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `CCCHAPS-PREVIEW-CTA-001-SAMPLE.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        return;
      }
      const r = await portalApi.get(`/portal/certificates/${certId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `CCCHAPS-${cert.cert_number}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatPortalError(err)); }
  };

  if (!learner || !cert) return (
    <div className="min-h-screen bg-[#FDFCF6]">
      {inPreview && <PreviewBanner />}
      <PortalHeader learner={learner}/>
      <div className="p-12 text-center text-slate-500">
        {inPreview ? "Complete the preview evaluation to see the sample certificate." : "Loading…"}
      </div>
    </div>
  );

  const dt = new Date(cert.completion_date);
  const dateStr = dt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const isSample = !!cert.sample;

  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-certificate">
      {inPreview && <PreviewBanner />}
      <PortalHeader learner={learner}/>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/academy/portal/dashboard" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-4"><ArrowLeft className="w-4 h-4"/> Back to dashboard</Link>

        {isSample && (
          <div data-testid="cert-sample-notice" className="mb-4 rounded-xl border-2 border-[#B8935A] bg-[#FBF7EC] p-4 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-[#B8935A] mt-0.5 shrink-0"/>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0F172A]">SAMPLE — NOT A VALID CERTIFICATE</p>
              <p className="mt-1 text-sm text-slate-700">This is a preview of what learners see. It has not been issued, does not count toward Certificates Issued, and its number is not a valid CTA-001 credential.</p>
            </div>
          </div>
        )}

        {/* Web certificate preview */}
        <div className={`bg-[#FDFCF6] border-[3px] ${isSample ? "border-[#B8935A]" : "border-slate-900"} rounded-2xl overflow-hidden shadow-lg relative`}>
          {isSample && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
              <span className="text-[#B8935A]/25 font-heading font-black text-6xl sm:text-8xl tracking-widest rotate-[-24deg] select-none whitespace-nowrap">SAMPLE — NOT VALID</span>
            </div>
          )}
          <div className={`border-2 ${isSample ? "border-slate-900" : "border-[#B8935A]"} m-2.5 p-6 md:p-10 text-center relative`}>
            <p className="font-heading text-2xl font-bold text-slate-900">CCCHAPS</p>
            <p className="text-xs uppercase tracking-[0.24em] text-slate-500 mt-1">Chaplain Training Academy</p>
            <p className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-8">Certificate of Completion</p>
            <div className="w-40 h-px bg-[#B8935A] mx-auto my-4"/>
            <p className="italic text-slate-600 mt-4">This certificate is presented to</p>
            <p className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4" data-testid="cert-name">{cert.participant_full_name}</p>
            <div className="w-64 max-w-full h-px bg-[#B8935A] mx-auto mt-2"/>
            <p className="text-slate-600 mt-5">for successfully completing</p>
            <p className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mt-2">CTA-001 — Grief in the Workplace: What Every Chaplain Should Know</p>
            <p className="text-sm text-slate-500 mt-1">Introductory Workplace Chaplain Training • 60 Minutes</p>
            <p className="mt-6 text-[#4A6FA5] font-heading text-sm font-semibold">Recognize → Listen → Support → Assess → Refer</p>
            <div className="mt-8 grid grid-cols-3 gap-4 text-left">
              <div><p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Completion Date</p><p className="text-sm text-slate-900 mt-0.5">{dateStr}</p></div>
              <div className="text-center"><p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Knowledge Check</p><p className="text-sm text-slate-900 mt-0.5">{cert.knowledge_check_score} / {cert.knowledge_check_total} · Passed</p></div>
              <div className="text-right"><p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Certificate Number</p><p className="text-sm font-mono text-slate-900 mt-0.5" data-testid="cert-number">{cert.cert_number}</p></div>
            </div>
            <div className="mt-6 flex justify-between text-[10px] text-slate-500 border-t border-slate-200 pt-3">
              <span>Authorized by California Corporate Chaplains</span>
              <span>www.ccchaps.com · office@ccchaps.com</span>
            </div>
            <p className="mt-4 text-[10px] text-slate-500 italic leading-relaxed">Completion acknowledges participation in this introductory class; it does not confer professional licensure, clinical certification, or accredited continuing-education credit.</p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <button onClick={download} data-testid="cert-download" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"><Download className="w-4 h-4"/> {isSample ? "Download Sample PDF" : "Download PDF"}</button>
          <button onClick={()=>window.print()} data-testid="cert-print" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-700 font-medium hover:border-[#4A6FA5]"><Printer className="w-4 h-4"/> Print</button>
        </div>
      </div>
    </div>
  );
}
